import { IAPIRoutes } from "../../../../config";

export type IBillingRepository = ReturnType<typeof BillingRepositoryFactory>;

export const BillingRepositoryFactory = ({
  APIProvider,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, PUT, POST, DELETE } = APIProvider;

  /**
   * Payers
   */
  const GetPayers: IGetBillingPayers = async (params) => {
    if ("apiName" in params) {
      delete params.apiName;
    }
    return GET(APIRoutes.apiGetBillingPayers, params) as Promise<
      PaginatedListResponse<IGetBillingPayersResponse>
    >;
  };

  const UpdatePayer: IUpdateBillingPayer = async (payload) => {
    const { id, created_at, updated_at, ...bodyData } = payload;

    return PUT(
      APIRoutes.apiUpdateBillingPayer.replace("{id}", String(id)),
      bodyData
    );
  };

  const CreatePayer: ICreateBillingPayer = async (payload) => {
    return POST(APIRoutes.apiCreateBillingPayer, payload);
  };

  const DeletePayer: IDeleteBillingPayer = async (id) => {
    return DELETE(APIRoutes.apiDeleteBillingPayer.replace("{id}", String(id)));
  };

  /**
   * Providers
   */
  const GetProviders: IGetBillingProviders = async (params) => {
    if ("apiName" in params) {
      delete params.apiName;
    }
    return GET(APIRoutes.apiGetBillingProviders, params) as Promise<
      PaginatedListResponse<IGetBillingProvidersResponse>
    >;
  };

  const UpdateProvider: IUpdateBillingProvider = async (payload) => {
    const { id, created_at, updated_at, ...bodyData } = payload;

    return PUT(
      APIRoutes.apiUpdateBillingProvider.replace("{id}", String(id)),
      bodyData
    );
  };

  const CreateProvider: ICreateBillingProvider = async (payload) => {
    return POST(APIRoutes.apiCreateBillingProvider, payload);
  };

  const DeleteProvider: IDeleteBillingProvider = async (id) => {
    return DELETE(
      APIRoutes.apiDeleteBillingProvider.replace("{id}", String(id))
    );
  };

  /**
   * Entity
   */
  const GetEntities: IGetBillingEntity = async (payload) => {
    const params = payload;
    return GET(APIRoutes.apiGetBillingEntity, params) as Promise<{
      success: boolean;
      data: IGetBillingEntityResponse;
    }>;
  };

  const UpdateEntity: IUpdateBillingEntity = async (payload) => {
    const { id, created_at, updated_at, ...bodyData } = payload;

    return PUT(
      APIRoutes.apiUpdateBillingEntity.replace("{id}", String(id)),
      bodyData
    );
  };

  const CreateEntity: ICreateBillingEntity = async (payload) => {
    return POST(APIRoutes.apiCreateBillingEntity, payload);
  };

  const DeleteEntity: IDeleteBillingEntity = async (id) => {
    return DELETE(APIRoutes.apiDeleteBillingEntity.replace("{id}", String(id)));
  };

  /**
   * Rosters
   */
  const GetRosters: IGetBillingRoster = async (params) => {
    if ("apiName" in params) {
      delete params.apiName;
    }
    return GET(APIRoutes.apiGetBillingRosters, params) as Promise<
      PaginatedListResponse<IGetBillingRosterResponse>
    >;
  };

  const UpdateRoster: IUpdateBillingRoster = async (payload) => {
    const { id, created_at, updated_at, ...bodyData } = payload;

    return PUT(
      APIRoutes.apiUpdateBillingRoster.replace("{id}", String(id)),
      bodyData
    );
  };

  const CreateRoster: ICreateBillingRoster = async (payload) => {
    return POST(APIRoutes.apiCreateBillingRoster, payload);
  };
  const DeleteRoster: IDeleteBillingRoster = async (id) => {
    return DELETE(APIRoutes.apiDeleteBillingRoster.replace("{id}", String(id)));
  };

  /**
   * Patients
   */
  const GetPatients: IGetBillingPatients = async (params) => {
    if ("apiName" in params) {
      delete params.apiName;
    }
    return GET(APIRoutes.apiGetBillingPatients, params) as Promise<
      PaginatedListResponse<IGetBillingPatientsResponse>
    >;
  };

  const UpdatePatient: IUpdateBillingPatient = async (payload) => {
    const { id, created_at, updated_at, ...bodyData } = payload;

    return PUT(
      APIRoutes.apiUpdateBillingPatient.replace("{id}", String(id)),
      bodyData
    );
  };

  const CreatePatient: ICreateBillingPatient = async (payload) => {
    return POST(APIRoutes.apiCreateBillingPatient, payload);
  };

  const DeletePatient: IDeleteBillingPatient = async (id) => {
    return DELETE(
      APIRoutes.apiDeleteBillingPatient.replace("{id}", String(id))
    );
  };

  /**
   * Charges
   */
  const GetCharges: IGetBillingCharges = async (params) => {
    if ("apiName" in params) {
      delete params.apiName;
    }
    return GET(APIRoutes.apiGetBillingCharges, params) as Promise<
      PaginatedListResponse<IGetBillingChargesResponse>
    >;
  };

  const UpdateCharges: IUpdateBillingCharges = async (payload) => {
    const { id, created_at, updated_at, ...bodyData } = payload;

    return PUT(
      APIRoutes.apiUpdateBillingCharges.replace("{id}", String(id)),
      bodyData
    );
  };

  const CreateCharges: ICreateBillingCharges = async (payload) => {
    return POST(APIRoutes.apiCreateBillingCharges, payload);
  };

  const DeleteCharges: IDeleteBillingCharges = async (id) => {
    return DELETE(
      APIRoutes.apiDeleteBillingCharges.replace("{id}", String(id))
    );
  };

  const GetChargesStatusSummary = async (params: any) => {
    if ("apiName" in params) {
      delete params.apiName;
    }
    return GET(APIRoutes.apiGetBillingChargesStatusSummary, params) as Promise<{
      success: boolean;
      data: Array<{ status: string; count: number }>;
    }>;
  };

  /**
   * RTX eligibility: GET /rtx/eligibility/{patient_id}?dos_from=&dos_to=
   */
  const GetEligibility: IGetRtxEligibility = async (payload) => {
    const path = APIRoutes.apiGetPatientEligibility.replace(
      "{patient_id}",
      String(payload.patientId)
    );
    const params: Record<string, string> = {};
    if (payload.dos_from != null) params.dos_from = payload.dos_from;
    if (payload.dos_to != null) params.dos_to = payload.dos_to;
    return GET(
      path,
      Object.keys(params).length ? params : undefined
    ) as ReturnType<IGetRtxEligibility>;
  };

  return {
    GetPayers,
    UpdatePayer,
    CreatePayer,
    DeletePayer,

    GetProviders,
    UpdateProvider,
    CreateProvider,
    DeleteProvider,

    GetEntities,
    UpdateEntity,
    CreateEntity,
    DeleteEntity,

    GetRosters,
    UpdateRoster,
    CreateRoster,
    DeleteRoster,

    GetPatients,
    UpdatePatient,
    CreatePatient,
    DeletePatient,

    GetCharges,
    UpdateCharges,
    CreateCharges,
    DeleteCharges,
    GetChargesStatusSummary,
    GetEligibility,
  };
};
